import { Component, Mixins } from 'vue-property-decorator'
import { TCWUnexpectedError } from '@/plugins/TCWBaseError'
import DataGetComponent from '@/tcw/components/DataGetComponent'
import DataUpdateComponent from '@/tcw/components/DataUpdateComponent'
import { IFormStore, UpdatePayload } from '@/store/types'
import { InvalidFields, KeyInfo } from '@/components/common/form/types'
import { ApiGeneralSinglePayload, ApiPayloadValueType } from '../api/types'

/**
 * コンポーネント基底クラス
 */
@Component
export default class FormStoreComponent<T = ApiGeneralSinglePayload> extends Mixins(
  DataGetComponent,
  DataUpdateComponent
) {
  protected serverCheckInfo: InvalidFields = {}

  get formName(): string {
    return 'form'
  }

  /**
   * 継承側で実装
   */
  get storeModule(): IFormStore<T> {
    // 便宜的に勤怠パターンのフォームストアを返す
    return {} as IFormStore<T>
  }
  get keyInfo(): KeyInfo {
    return {}
  }
  get isEmptyRecord(): boolean {
    const keys = Object.keys(this.keyInfo)
    if (keys.length === 0) {
      return true
    }

    return keys.some(k => {
      return !this.keyInfo[k] || this.keyInfo[k] === ''
    })
  }

  /**
   * 変更検知
   */
  public get isChanged(): boolean {
    return this.storeModule.isChanged
  }
  /**
   * ロック有無
   */
  public get isLock(): boolean {
    return this.storeModule.isLock
  }

  protected async fetchData(): Promise<void> {
    await this.loadStore()
  }
  /**
   * サーバからフォームデータを取得する。
   * すでにサーバから取得済みの場合、取得対象のデータが同じ場合は取得済みデータを返す。
   * 取得対象と違う場合は確認画面を表示する。
   * ただし、force=trueの場合は取得済みにかかわらずサーバから再取得する。
   */
  protected async loadStore(force = false): Promise<void> {
    let form = {} as T

    // ロード済みデータのチェック
    if (this.storeModule.ready && !force) {
      // 保持済みデータと取得対象のデータが同一かチェック
      const isSameKey = this.checkSameKey(this.keyInfo, this.storeModule.editData)
      if (isSameKey) {
        // 同じデータなら使いまわす
        form = this.storeModule.editData
        this.responseInitWebApi(form)
        return
      }
    }

    if (this.isEmptyRecord) {
      form = await this.storeModule.fetchEmpty()
    } else {
      form = await this.storeModule.fetch(this.keyInfo)
    }
    this.responseInitWebApi(form)
  }
  protected checkSameKey(keyInfo: KeyInfo, editData: T): boolean {
    if (editData && typeof editData === 'object') {
      const keys = Object.keys(keyInfo)
      return keys.every(
        k => k in editData && keyInfo[k] == ((editData as unknown) as ApiGeneralSinglePayload)[k]
      )
    } else {
      return false
    }
  }

  /***************
   * 保存処理
   ***************/

  protected updateValue(fieldName: string, updateValue: ApiPayloadValueType): void {
    const payload: UpdatePayload = {}
    payload[fieldName] = updateValue
    this.storeModule.updateRecord(payload)
  }

  protected async validate(): Promise<void> {
    const form = this.$refs[this.formName] as any
    if (form === undefined) {
      // コーディングミスのため普通は起こりえない
      throw new TCWUnexpectedError(
        'データ構造が正しくありません。システム管理者に問い合わせてください。'
      )
    }
    if (!(form.validate instanceof Function)) {
      // コーディングミスのため普通は起こりえない
      throw new TCWUnexpectedError(
        'データ構造が正しくありません。システム管理者に問い合わせてください。'
      )
    }
    return await form.validate()
  }
  protected async execSaveMain(): Promise<void> {
    // 保存処理
    await this.storeModule.submit(this.keyInfo)
  }
  protected async afterSaveProc(): Promise<void> {
    this.clearForm()

    // 保存後にフォームクリア
    this.$tcw.message({
      type: 'success',
      message: '保存しました'
    })
    // 前画面に戻る
    this.back()
  }

  protected clearForm(): void {
    this.storeModule.clear()
  }
}
